// Static explainer content for the indicator cards, keyed by card label.
// Every factual claim mirrors the strategy 1 processor code
// (src/stocks_analyzer/service/strategies/strategy_1/processors/) and the
// scoring engine's blend/veto rules (strategy_1/scoring.py).
export interface IndicatorInfo {
  whatItIs: string;      // plain-language definition
  role: string;          // voter family / referee / veto-only, + the 0=buy/100=sell convention
  scoreMoves: string[];  // bullets: what pushes toward 0 (buy) vs 100 (sell)
}

export const INDICATOR_INFO: Record<string, IndicatorInfo> = {
  RSI: {
    whatItIs: '14-day Relative Strength Index — an oscillator (0–100) comparing the size of '
      + 'recent gains to recent losses. Low readings mean losses have dominated (oversold); '
      + 'high readings mean gains have dominated (overbought).',
    role: 'One of 2 reversion votes (with Bollinger Bands) on the 0 = buy … 100 = sell scale; '
      + 'weighted most heavily when the ADX referee says the market is ranging.',
    scoreMoves: [
      'Score is RSI’s position between the 30 (oversold) and 70 (overbought) bands mapped '
        + 'to 0–100: at/below 30 → 0 (buy), at/above 70 → 100 (sell).',
      'Falls toward buy when losses have dominated the last 14 days (RSI sinking toward 30); '
        + 'rises toward sell when gains have dominated (RSI climbing toward 70).',
      'Extreme readings (below 10 / above 90) additionally trigger the veto system.',
    ],
  },
  'Bollinger Bands': {
    whatItIs: 'A 20-day moving average with bands 2 standard deviations above and below it — '
      + 'a statistical envelope of where price normally trades.',
    role: 'One of 2 reversion votes (with RSI) on the 0 = buy … 100 = sell scale; weighted '
      + 'most heavily when the ADX referee says the market is ranging.',
    scoreMoves: [
      'Score is the day’s position between the bands, measured from the day’s high to '
        + 'each band: at/below the lower band → 0 (buy), at/above the upper band → 100 '
        + '(sell), proportional in between.',
      'Falls toward buy when price stretches unusually far below its 20-day average; rises '
        + 'toward sell when it stretches above.',
      'A candle entirely outside the bands pins the score to the extreme (below → 0, '
        + 'above → 100).',
      'A close beyond a band by more than half the band width triggers the veto system '
        + '(above the upper band blocks sells, below the lower band blocks buys).',
    ],
  },
  MACD: {
    whatItIs: 'Moving Average Convergence Divergence — the gap between the 12-day and 26-day '
      + 'EMAs (the MACD line) tracked against its own 9-day EMA (the signal line); the '
      + 'histogram is their difference.',
    role: 'One of 6 trend votes on the 0 = buy … 100 = sell scale; weighted most heavily when '
      + 'the ADX referee says a trend is in force.',
    scoreMoves: [
      'Starts from the histogram’s position within its recent range: strongly positive → '
        + 'near 0 (buy), strongly negative → near 100 (sell).',
      'MACD line above the signal line then pulls the score 30% closer to buy; below the '
        + 'signal line, 30% closer to sell.',
      'A flat histogram range yields a neutral 50.',
    ],
  },
  ADX: {
    whatItIs: '14-day Average Directional Index — a 0–100 gauge of trend strength (not '
      + 'direction), derived from the +DI/−DI directional indicators.',
    role: 'Referee — no vote. Instead of scoring on the 0 = buy … 100 = sell scale itself, it '
      + 'sets the blend between the trend and reversion families.',
    scoreMoves: [
      'Below ADX_BLEND_LOW (default 20): the final score is 100% the reversion family '
        + '(RSI + Bollinger Bands).',
      'Above ADX_BLEND_HIGH (default 40): the final score is 100% the trend family.',
      'Linear ramp in between; an unreadable ADX falls back to a 50/50 blend.',
      'Separately, ADX above ~75 with a sharp recent +DI/−DI reversal vetoes trading '
        + 'against that reversal.',
    ],
  },
  'Parabolic SAR': {
    whatItIs: 'Stop-and-reverse dots that trail the price — below it in an uptrend, above it '
      + 'in a downtrend, flipping sides when price crosses them.',
    role: 'One of 6 trend votes on the 0 = buy … 100 = sell scale; weighted most heavily when '
      + 'the ADX referee says a trend is in force.',
    scoreMoves: [
      'Uptrend (SAR below price): the score starts at 30 and falls toward 0 (buy) as the '
        + 'price–SAR gap widens.',
      'Downtrend (SAR above price): the score starts at 70 and rises toward 100 (sell) as '
        + 'the gap widens.',
      'A fresh SAR flip with a >3% three-day price move triggers the veto system against '
        + 'trading into the reversal (a flip to uptrend blocks sells, to downtrend blocks buys).',
    ],
  },
  ATR: {
    whatItIs: '14-day Average True Range — the typical size of a day’s price movement, a '
      + 'pure volatility gauge.',
    role: 'Veto-only — never votes. It contributes nothing to the 0 = buy … 100 = sell score; '
      + 'it can only block sells.',
    scoreMoves: [
      'No score contribution — the chart is context only.',
      'When the current ATR as a % of price exceeds ~2× its average over the window, it '
        + 'raises a sell veto: don’t sell into extreme volatility.',
    ],
  },
  OBV: {
    whatItIs: 'On-Balance Volume — a running total of volume, added on up days and subtracted '
      + 'on down days, showing whether volume is flowing into or out of the stock.',
    role: 'One of 6 trend votes on the 0 = buy … 100 = sell scale; weighted most heavily when '
      + 'the ADX referee says a trend is in force.',
    scoreMoves: [
      'Compares the 10-day price trend against the 10-day OBV trend.',
      'Both rising (bullish confirmation) → 20; price falling but OBV rising (bullish '
        + 'divergence) → 35.',
      'Price rising but OBV falling (bearish divergence) → 65; both falling (bearish '
        + 'confirmation) → 80.',
      '±5 on top depending on whether OBV sits above or below its own 20-day average.',
    ],
  },
  'MA Anchor': {
    whatItIs: 'Long-horizon trend structure from the 50- and 200-day simple moving averages — '
      + 'the chart shows the close with both SMAs.',
    role: 'One of 6 trend votes on the 0 = buy … 100 = sell scale; weighted most heavily when '
      + 'the ADX referee says a trend is in force.',
    scoreMoves: [
      'Four bullish checks worth 25 points each; score = 100 − 25 × checks passed.',
      'The checks: price above the 50-day SMA, price above the 200-day SMA, 50-day SMA above '
        + '200-day SMA, and the 200-day SMA rising vs ~1 month ago.',
      'All four pass → 0 (strongest buy); none pass → 100 (strongest sell).',
      'Needs about 221 bars of history; shorter history votes a neutral 50.',
    ],
  },
  'RS vs SPY': {
    whatItIs: 'Relative strength against the market: the stock’s ~6-month (126-trading-day) '
      + 'return minus SPY’s — the chart shows both series rebased to 100 plus the rolling '
      + 'spread.',
    role: 'One of 6 trend votes on the 0 = buy … 100 = sell scale; weighted most heavily when '
      + 'the ADX referee says a trend is in force.',
    scoreMoves: [
      'Outperforming SPY pushes the score toward 0 (buy); underperforming pushes it toward '
        + '100 (sell).',
      '±40% relative performance saturates the score at 0 or 100.',
      'A missing or too-short benchmark series votes a neutral 50.',
    ],
  },
  'DI Direction': {
    whatItIs: 'Directional bias from the +DI vs −DI directional indicators (the same lines '
      + 'that feed ADX) — computed by the ADX pass and shown on its own chart.',
    role: 'One of 6 trend votes on the 0 = buy … 100 = sell scale; weighted most heavily when '
      + 'the ADX referee says a trend is in force.',
    scoreMoves: [
      'Score = 50 shifted by the DI imbalance: all-bullish +DI → 0 (buy), all-bearish '
        + '−DI → 100 (sell), balanced → 50.',
      'Trend strength is deliberately excluded — that’s the ADX referee’s job; this '
        + 'voter carries direction only.',
    ],
  },
};
